/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/shared/validateA3Form.ts.
 */

import type {
  FormDataResponse,
  FormField,
} from "../../../../../interfaces/formInterfaces";
import type { FuelDensityApiRow } from "../../../../../services/a1Service";
import type { CalorificValuePublic } from "../../../../../services/calorificValuesPublicService";
import {
  isA3EmbodiedCarbonLockedManufacturingProcessField,
  isA3EmbodiedEnergyLockedManufacturingProcessField,
  isA3FuelLockedManufacturingProcessField,
} from "../../../../../utils/a3ManufacturingProcessResolution";
import { isA3ManufacturingSpecificityFromBackgroundField } from "../../../../../utils/a3ManufacturingSpecificityField";
import { A3_MAX_FUELS_PER_PROCESS } from "../A3/utils/a3ProcessFuelLayout";
import {
  type ContributorFormErrors,
  getFirstFormError,
} from "./contributorFormValidation";
import { validateContributorSectionRows } from "./validateContributorSectionRows";
import {
  isA3PlantMaterialQualityField,
  rawMaterialQualityUsesCatalogAttributes,
  validateA3PlantMaterialQualityFields,
} from "../../../../../utils/a3RawMaterialQualityField";
import {
  shouldSkipA3CalorificValueMandatoryValidation,
  shouldSkipA3FuelDensityMandatoryValidation,
} from "./fuelMixDensityValidation";

export { getFirstFormError };

const A3_PROCESS_DETAILS = "A3_PROCESS_DETAILS";
const A3_PROCESS_FUEL_DETAILS = "A3_PROCESS_FUEL_DETAILS";
const A3_CARBON_CODE = "A3_EMBODIED_CARBON_DETAILS";
const A3_ENERGY_CODE = "A3_EMBODIED_ENERGY_DETAILS";

function totalFuelFlatRows(fuelsPerProcess: number[]): number {
  return fuelsPerProcess.reduce(
    (sum, c) => sum + Math.min(A3_MAX_FUELS_PER_PROCESS, Math.max(1, c)),
    0
  );
}

function shouldSkipA3Field(
  field: FormField,
  sectionCode: string,
  rowIndex: number,
  sectionName?: string
): boolean {
  if (!field.isEnabled) return true;
  if (field.type === "READONLY" || field.isEditable === false) return true;
  if (field.type === "RAW_MATERIALS" && sectionCode === A3_PROCESS_DETAILS) {
    return true;
  }
  if (
    rowIndex === 0 &&
    isA3ManufacturingSpecificityFromBackgroundField(
      field,
      sectionCode,
      sectionName
    )
  ) {
    return true;
  }
  if (isA3FuelLockedManufacturingProcessField(field, sectionCode)) return true;
  if (isA3EmbodiedEnergyLockedManufacturingProcessField(field, sectionCode)) {
    return true;
  }
  if (isA3EmbodiedCarbonLockedManufacturingProcessField(field, sectionCode)) {
    return true;
  }
  if (
    field.type === "RAW_MATERIAL_QUALITY" &&
    rawMaterialQualityUsesCatalogAttributes(field, sectionCode)
  ) {
    return true;
  }
  if (isA3PlantMaterialQualityField(field, sectionCode)) {
    return true;
  }
  return false;
}

/** Exported function — validate A3Form. */
export function validateA3Form({
  formData,
  values,
  processDetailsRowCount,
  fuelsPerProcess,
  fuelDensityCatalog = [],
  calorificValuesCatalog = [],
}: {
  formData: FormDataResponse;
  values: Record<string, string>;
  processDetailsRowCount: number;
  fuelsPerProcess: number[];
  fuelDensityCatalog?: FuelDensityApiRow[];
  calorificValuesCatalog?: CalorificValuePublic[];
}): ContributorFormErrors {
  const sections = formData.formSubSections || [];

  const rowErrors = validateContributorSectionRows({
    sections,
    sectionRows: {
      [A3_PROCESS_DETAILS]: processDetailsRowCount,
      [A3_CARBON_CODE]: processDetailsRowCount,
      [A3_ENERGY_CODE]: processDetailsRowCount,
      [A3_PROCESS_FUEL_DETAILS]: Math.max(1, totalFuelFlatRows(fuelsPerProcess)),
    },
    values,
    getRowCount: (sectionCode, defaultRowCount) => {
      if (
        sectionCode === A3_PROCESS_DETAILS ||
        sectionCode === A3_CARBON_CODE ||
        sectionCode === A3_ENERGY_CODE
      ) {
        return Math.max(1, processDetailsRowCount);
      }
      if (sectionCode === A3_PROCESS_FUEL_DETAILS) {
        return Math.max(1, totalFuelFlatRows(fuelsPerProcess));
      }
      return defaultRowCount;
    },
    shouldSkipField: (field, sectionCode, rowIndex) => {
      const section = sections.find(
        (s) => (s.code || s.name || String(s.id)) === sectionCode
      );
      if (
        shouldSkipA3Field(
          field,
          sectionCode,
          rowIndex,
          section?.name
        )
      ) {
        return true;
      }
      return (
        shouldSkipA3FuelDensityMandatoryValidation({
          field,
          sectionCode,
          rowIndex,
          values,
          formData,
          fuelDensityCatalog,
        }) ||
        shouldSkipA3CalorificValueMandatoryValidation({
          field,
          sectionCode,
          rowIndex,
          values,
          formData,
          calorificValuesCatalog,
        })
      );
    },
  });

  const qualityErrors = validateA3PlantMaterialQualityFields({
    sections,
    values,
  });

  return { ...rowErrors, ...qualityErrors };
}
